'use client'

/**
 * The side navigation's two action rows (#15618): Search and Send feedback.
 * The Notifications row is `sidebar-notifications-item.tsx`.
 *
 * Shared by `AppSidebar` (wherever the org's nav layout places them) and
 * `AdminSideNav` (#15688), whose header is bar-less too, so it must carry
 * them. Each opens the SAME instance the header owns — the command bar and
 * the feedback modal are read through `app-chrome-context` (and
 * `useFeedbackEntry`), never mounted twice.
 */

import { SidebarMenuButton, SidebarMenuItem } from '@sai/ui/components'

import { FEEDBACK_MODE_EXEMPT_ATTRIBUTE } from '@/components/feedback/feedback-mode-attributes'
import { useFeedbackEntry } from '@/components/feedback/use-feedback-entry'
import { MessageSquarePlus } from '@/components/icons'
import { useAppChromeOverlay } from '@/contexts/app-chrome-context'
import { ENTRIES } from '@/lib/org-nav/vocabulary'

import { SIDEBAR_ROW_FOCUS_RING } from './sidebar-focus-ring'

interface SidebarActionItemProps {
  /** Closes the mobile sheet. */
  onNavigate: () => void
}

export function SidebarSearchItem({ onNavigate }: SidebarActionItemProps) {
  const [, setCommandBarOpen] = useAppChromeOverlay('commandBar')
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        tooltip="Search"
        className={SIDEBAR_ROW_FOCUS_RING}
        data-testid="nav-search"
        aria-keyshortcuts="Meta+K Control+K Shift+K"
        onClick={() => {
          onNavigate()
          setCommandBarOpen(true)
        }}
      >
        {/* No visible shortcut hint (#15668); `aria-keyshortcuts` keeps it
            for assistive technology. */}
        <ENTRIES.search.icon />
        <span>Search</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

export function SidebarFeedbackItem({ onNavigate }: SidebarActionItemProps) {
  const feedback = useFeedbackEntry()
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        tooltip="Send feedback"
        className={SIDEBAR_ROW_FOCUS_RING}
        data-testid="nav-feedback"
        onClick={() => {
          onNavigate()
          feedback.open()
        }}
        // Without this the picker anchors feedback to the very row that
        // opened it (#13715), as for the header button.
        {...{ [FEEDBACK_MODE_EXEMPT_ATTRIBUTE]: 'true' }}
      >
        <MessageSquarePlus />
        <span>Send feedback</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}
